import type { PlaceholderValue } from "@homeinstead/schemas";
import type { PortableTextComponents } from "@portabletext/react";
import type { PortableTextBlock } from "@portabletext/types";
import type { ReactNode } from "react";
import type { HeadingFont, RichTextComponentsFactory } from "./types";

import { isEmptyValue } from "@homeinstead/schemas";
import {
  extractPlainTextFromBlocks,
  isValidPortableText,
} from "../../utils/filter-empty-blocks";
import { renderPortableTextInline } from "./render-portable-text-inline";
import { replaceChildrenText } from "./replace-children-text";

type CreateDynamicPlaceholderMarkOptions = {
  activePlaceholderKeys: readonly string[];
  components?: Partial<PortableTextComponents>;
  createRichTextComponents: RichTextComponentsFactory;
  includeAccordion?: boolean;
  includeColumns?: boolean;
  headingFont?: HeadingFont;
  onLinkClick?: (href: string) => void;
  placeholders: Record<string, PlaceholderValue>;
};

type PlaceholderRenderContext = CreateDynamicPlaceholderMarkOptions & {
  children: ReactNode;
  hideWhenEmpty?: boolean;
  isRecursivePlaceholder: boolean;
  placeholderKey?: string;
};

function warnAboutPlaceholderCycle(
  placeholderKey: string,
  activePlaceholderKeys: readonly string[],
) {
  console.warn(
    `Skipping recursive dynamic placeholder "${placeholderKey}" while rendering RichText. Active placeholder chain: ${[
      ...activePlaceholderKeys,
      placeholderKey,
    ].join(" -> ")}`,
  );
}

function renderPortableTextValue(
  blocks: PortableTextBlock[],
  context: PlaceholderRenderContext,
  nextActivePlaceholderKeys: readonly string[],
) {
  return renderPortableTextInline(
    blocks,
    context.createRichTextComponents({
      activePlaceholderKeys: nextActivePlaceholderKeys,
      components: context.components,
      headingFont: context.headingFont,
      includeAccordion: context.includeAccordion,
      includeColumns: context.includeColumns,
      onLinkClick: context.onLinkClick,
      placeholders: context.placeholders,
    }),
  );
}

function renderPlainTextFromBlocks(
  blocks: PortableTextBlock[],
  children: ReactNode,
) {
  const plainText = extractPlainTextFromBlocks(blocks).trim();
  if (!plainText) {
    return null;
  }

  return <>{replaceChildrenText(children, plainText)}</>;
}

function renderPlaceholderValue(
  actualValue: PlaceholderValue,
  context: PlaceholderRenderContext,
): ReactNode | null {
  const { children, hideWhenEmpty, placeholderKey } = context;

  if (Array.isArray(actualValue) && isValidPortableText(actualValue)) {
    const nextActivePlaceholderKeys =
      typeof placeholderKey === "string"
        ? [...context.activePlaceholderKeys, placeholderKey]
        : context.activePlaceholderKeys;
    const rendered = renderPortableTextValue(
      actualValue as PortableTextBlock[],
      context,
      nextActivePlaceholderKeys,
    );

    if (rendered !== null) {
      return rendered;
    }

    return hideWhenEmpty ? null : undefined;
  }

  if (Array.isArray(actualValue)) {
    const plainTextResult = renderPlainTextFromBlocks(
      actualValue as PortableTextBlock[],
      children,
    );

    if (plainTextResult) {
      return plainTextResult;
    }

    return hideWhenEmpty ? null : undefined;
  }

  return <>{replaceChildrenText(children, String(actualValue))}</>;
}

function renderPlaceholderFallback(
  fallbackText: PlaceholderValue | undefined,
  context: PlaceholderRenderContext,
): ReactNode | null {
  const { children, isRecursivePlaceholder } = context;

  if (
    !isRecursivePlaceholder &&
    Array.isArray(fallbackText) &&
    isValidPortableText(fallbackText)
  ) {
    const rendered = renderPortableTextValue(
      fallbackText as PortableTextBlock[],
      context,
      context.activePlaceholderKeys,
    );

    if (rendered !== null) {
      return rendered;
    }
  }

  if (Array.isArray(fallbackText)) {
    const plainTextResult = renderPlainTextFromBlocks(
      fallbackText as PortableTextBlock[],
      children,
    );

    if (plainTextResult) {
      return plainTextResult;
    }
  }

  if (typeof fallbackText === "string" && fallbackText) {
    return <>{replaceChildrenText(children, fallbackText)}</>;
  }

  return null;
}

function createDynamicPlaceholderMark({
  activePlaceholderKeys,
  components,
  createRichTextComponents,
  includeAccordion,
  includeColumns,
  headingFont,
  onLinkClick,
  placeholders,
}: CreateDynamicPlaceholderMarkOptions): NonNullable<
  PortableTextComponents["marks"]
>["dynamicPlaceholder"] {
  const DynamicPlaceholderMark: NonNullable<
    PortableTextComponents["marks"]
  >["dynamicPlaceholder"] = ({ children, value }) => {
    const hideWhenEmpty = value?.hideWhenEmpty;
    const placeholderKey = value?.placeholderKey;
    const isRecursivePlaceholder =
      typeof placeholderKey === "string" &&
      activePlaceholderKeys.includes(placeholderKey);
    const actualValue = isRecursivePlaceholder
      ? undefined
      : placeholders[placeholderKey];
    const context: PlaceholderRenderContext = {
      activePlaceholderKeys,
      children,
      components,
      createRichTextComponents,
      headingFont,
      hideWhenEmpty,
      includeAccordion,
      includeColumns,
      isRecursivePlaceholder,
      onLinkClick,
      placeholderKey,
      placeholders,
    };

    if (isRecursivePlaceholder) {
      warnAboutPlaceholderCycle(placeholderKey, activePlaceholderKeys);
    }

    if (hideWhenEmpty && isEmptyValue(actualValue)) {
      return null;
    }

    if (actualValue != null) {
      const renderedValue = renderPlaceholderValue(actualValue, context);
      if (renderedValue !== undefined) {
        return renderedValue;
      }
    }

    const renderedFallback = renderPlaceholderFallback(
      value?.fallbackText,
      context,
    );

    if (renderedFallback !== null) {
      return renderedFallback;
    }

    return <>{children}</>;
  };

  DynamicPlaceholderMark.displayName = "DynamicPlaceholderMark";

  return DynamicPlaceholderMark;
}

export { createDynamicPlaceholderMark };
